import type { StrokeEvent } from './strokes';

/** Versioned on-disk / on-wire format for a world's paint. */
export interface WorldExport {
  format: 'snow-graffiti-world';
  version: 1;
  locationId: string;
  exportedAt: string;
  strokes: StrokeEvent[];
}

export function exportWorld(locationId: string, strokes: readonly StrokeEvent[]): string {
  const data: WorldExport = {
    format: 'snow-graffiti-world',
    version: 1,
    locationId,
    exportedAt: new Date().toISOString(),
    strokes: strokes as StrokeEvent[],
  };
  return JSON.stringify(data);
}

function isFiniteNumber(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

function isVec3(v: unknown): v is [number, number, number] {
  return Array.isArray(v) && v.length === 3 && v.every(isFiniteNumber);
}

function isVec2(v: unknown): v is [number, number] {
  return Array.isArray(v) && v.length === 2 && v.every(isFiniteNumber);
}

function isStroke(s: unknown): s is StrokeEvent {
  if (typeof s !== 'object' || s === null) return false;
  const e = s as Record<string, unknown>;
  return (
    (e.kind === 'spray' || e.kind === 'footprint') &&
    typeof e.locationId === 'string' &&
    typeof e.surfaceId === 'string' &&
    isVec3(e.pos) &&
    isVec2(e.uv) &&
    isFiniteNumber(e.radius) &&
    isFiniteNumber(e.opacity) &&
    typeof e.color === 'string' &&
    /^#[0-9a-fA-F]{6}$/.test(e.color) &&
    isFiniteNumber(e.seed) &&
    isFiniteNumber(e.t)
  );
}

/** Parse and validate an exported world. Throws with a friendly message on bad input. */
export function importWorld(json: string): WorldExport {
  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    throw new Error('That file is not valid JSON.');
  }
  if (
    typeof raw !== 'object' ||
    raw === null ||
    (raw as Record<string, unknown>).format !== 'snow-graffiti-world'
  ) {
    throw new Error('That file is not a Snow Graffiti world export.');
  }
  const data = raw as Record<string, unknown>;
  if (data.version !== 1) throw new Error(`Unsupported world version: ${String(data.version)}`);
  if (typeof data.locationId !== 'string') throw new Error('World file is missing a location id.');
  if (!Array.isArray(data.strokes) || !data.strokes.every(isStroke)) {
    throw new Error('World file contains invalid stroke data.');
  }
  return raw as unknown as WorldExport;
}
